import { Action, ThunkAction, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import snackbarReducer from "components/action-snackbar/action-snackbar-slice";
import confirmDialogReducer from "components/confirm-dialog/confirm-dialog.slice";
import filtersReducer from "components/filters-bar/filters-slice";
import sectionSideMenuReducer from "components/section-side-menu/section-side-menu.slice";
import dialerReducer from "components/suno-dialer/suno-dialer-slice";
import wordProcessorSlice from "components/word-processor-field/word-processor.slice";
import authReducer from "features/auth/auth-slice";
import billingFiltersReducer from "features/billing/invoices/billing-filters-slice";
import collectionsFiltersReducer from "features/collections/collections-list/collections-filters-slice";
import outreachScheduleFiltersReducer from "features/collections/outreach-schedule/outreach-schedule-filters-slice";
import inboxReducer from "features/inbox/inbox-slice";
import leadsSlice from "features/leads-ui/leads-ui.slice";
import medicalScribeSlice from "features/medical-scribe/medical-scribe.slice";
import notificationsSlice from "features/notifications/notifications.slice";
import activeVisitDrawerReducer from "features/patients/active-visit-drawer/active-visit-drawer.slice";
import patientHeaderSlice from "features/patients/patient-header/patient-header-slice";
import patientFiltersReducer from "features/patients/patients-list/patient-filters-slice";
import patientChartNotesReducer from "features/patients/sections/patient-chart-notes/patient-chart-notes.slice";
import hearingAidsWizardReducer from "features/patients/sections/patient-hearing-aids/new-hearing-aid/new-hearing-aid-wizard/state/hearing-aids-wizard-slice";
import accountsReceivableSlice from "features/reports/accounts-receivable/accounts-receivable.slice";
import invoicesReceivableFiltersReducer from "features/reports/invoices-receivable/invoices-receivable-filters/invoices-receivable-filters.slice";
import roomTrafficReducer from "features/scheduling/room-traffic/room-traffic.slice";
import appointmentFiltersReducer from "features/scheduling/scheduler-slice";
import refundReceiptModalReducer from "features/patients/sections/patient-billing/refund-receipt/refund-receipt-modal.slice";
import { emptySplitApi } from "services/empty-split-api";
import { sunoCommsApi } from "services/suno-comms/suno-comms-api";
import { rtkQueryErrorLogger } from "utils/services.util";
import { viteEnv } from "utils/vite-env.util";

export const store = configureStore({
  reducer: {
    auth: authReducer,
    confirmDialog: confirmDialogReducer,
    activeVisitDrawer: activeVisitDrawerReducer,
    patientHeader: patientHeaderSlice,
    patientFilters: patientFiltersReducer,
    patientChartNotes: patientChartNotesReducer,
    billingFilters: billingFiltersReducer,
    collectionsFilters: collectionsFiltersReducer,
    outreachScheduleFilters: outreachScheduleFiltersReducer,
    appointmentFilters: appointmentFiltersReducer,
    sectionSideMenu: sectionSideMenuReducer,
    filters: filtersReducer,
    hearingAidsWizard: hearingAidsWizardReducer,
    snackbar: snackbarReducer,
    dialer: dialerReducer,
    inbox: inboxReducer,
    wordProcessor: wordProcessorSlice,
    notifications: notificationsSlice,
    medicalScribe: medicalScribeSlice,
    accountsReceivable: accountsReceivableSlice,
    invoicesReceivableFilters: invoicesReceivableFiltersReducer,
    roomTraffic: roomTrafficReducer,
    leads: leadsSlice,
    refundReceiptModal: refundReceiptModalReducer,
    [emptySplitApi.reducerPath]: emptySplitApi.reducer,
    [sunoCommsApi.reducerPath]: sunoCommsApi.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false // Ignore `A non-serializable value` errors,
    })
      .concat(emptySplitApi.middleware)
      .concat(sunoCommsApi.middleware)
      .concat(rtkQueryErrorLogger),
  devTools: viteEnv.VITE_ENV !== "prod"
});

setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
